import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { AlertTriangle, CheckCircle, Clock, Plus } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const API = `${window.__BACKEND_URL__}/api`;

const ACTIVITY_TYPES = [
  ['cleaning', 'Cleaning'], ['maintenance', 'Maintenance'], ['snow_removal', 'Snow Removal'],
  ['bus_cleaning', 'Bus Cleaning'], ['travel', 'Travel'], ['training', 'Training'],
  ['safety_meeting', 'Safety Meeting'], ['shop_yard', 'Shop/Yard'], ['waiting', 'Waiting'],
  ['administration', 'Administration'], ['other', 'Other'],
];
const ACTIVITY_LABELS = Object.fromEntries(ACTIVITY_TYPES);

const EXCEPTION_EXPLAINERS = {
  missing_clock_out: 'This shift has no clock-out recorded.',
  shift_too_long: 'This shift is longer than your company allows.',
  missing_break: 'No break was recorded on a long shift.',
  overtime: 'This day went over the daily overtime threshold.',
  overlapping_entries: 'This shift overlaps another one of your shifts.',
  duplicate_entry: 'This looks like a duplicate of another shift.',
  manual_time_change: 'The times on this shift were manually edited.',
  job_hours_mismatch: 'Your job allocations don\u2019t add up to your payable hours.',
  missing_job_allocation: 'No job or activity hours have been allocated yet.',
  payroll_exception: 'Payroll flagged this shift for review.',
};

const utcDate = (v) => new Date(typeof v === 'string' && !v.endsWith('Z') && !v.includes('+') ? `${v}Z` : v);
const fmtTime = (v) => utcDate(v).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
const fmtDate = (v) => utcDate(v).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
const entryHours = (e) => e.clock_out ? Math.max(0, (utcDate(e.clock_out) - utcDate(e.clock_in)) / 3600000) : 0;

const AllocationForm = ({ token, entryId, jobs, reconciliation, onAdded, onClose }) => {
  const { toast } = useToast();
  const [form, setForm] = useState({ job_id: 'none', activity_type: 'cleaning', hours: '', activity_description: '' });
  const [busy, setBusy] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const add = async () => {
    const hours = parseFloat(form.hours);
    if (!hours || hours <= 0) {
      toast({ title: 'Enter the hours for this allocation', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      const job = jobs.find((j) => j.id === form.job_id);
      await axios.post(`${API}/time/${entryId}/allocations`, {
        job_id: form.job_id === 'none' ? null : form.job_id,
        job_name: job ? (job.title || job.job_number) : null,
        activity_type: form.activity_type,
        hours,
        activity_description: form.activity_description || null,
      }, auth);
      onAdded();
      onClose();
    } catch (e) {
      toast({ title: 'Could not add allocation', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="allocation-form-dialog">
        <DialogHeader>
          <DialogTitle>Add job/activity hours</DialogTitle>
          {reconciliation && (
            <DialogDescription data-testid="allocation-reconciliation-bar">
              Allocated: {reconciliation.allocated_hours.toFixed(2)} / {reconciliation.payroll_hours.toFixed(2)} hrs
            </DialogDescription>
          )}
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Job</Label>
            <Select value={form.job_id} onValueChange={(v) => setForm({ ...form, job_id: v })}>
              <SelectTrigger data-testid="allocation-job-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No specific job</SelectItem>
                {jobs.map((j) => (
                  <SelectItem key={j.id} value={j.id}>{j.job_number ? `${j.job_number} — ` : ''}{j.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Activity</Label>
            <Select value={form.activity_type} onValueChange={(v) => setForm({ ...form, activity_type: v })}>
              <SelectTrigger data-testid="allocation-activity-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ACTIVITY_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Hours</Label>
            <Input type="number" step="0.25" min="0.25" value={form.hours}
              onChange={(e) => setForm({ ...form, hours: e.target.value })}
              data-testid="allocation-hours-input" />
          </div>
          {form.activity_type === 'other' && (
            <div>
              <Label className="text-xs">What did you do? (required)</Label>
              <Input value={form.activity_description}
                onChange={(e) => setForm({ ...form, activity_description: e.target.value })}
                data-testid="allocation-description-input" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="allocation-cancel-btn">Cancel</Button>
          <Button onClick={add} disabled={busy} className="bg-[#00C896] hover:bg-emerald-600 text-white" data-testid="allocation-add-btn">
            {busy ? 'Adding…' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AmendmentForm = ({ token, entry, onClose }) => {
  const { toast } = useToast();
  const [form, setForm] = useState({ requested_clock_in: '', requested_clock_out: '', reason: '' });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.requested_clock_in && !form.requested_clock_out) {
      toast({ title: 'Enter the corrected clock-in and/or clock-out time', variant: 'destructive' });
      return;
    }
    if (form.reason.trim().length < 10) {
      toast({ title: 'Please explain the correction (at least 10 characters)', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/time/amendments`, {
        job_id: entry.project_id || null,
        requested_clock_in: form.requested_clock_in ? new Date(form.requested_clock_in).toISOString() : null,
        requested_clock_out: form.requested_clock_out ? new Date(form.requested_clock_out).toISOString() : null,
        reason: form.reason.trim(),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Correction requested', description: 'Your supervisor has been notified.' });
      onClose();
    } catch (e) {
      toast({ title: 'Could not request correction', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="amendment-form-dialog">
        <DialogHeader>
          <DialogTitle>Request a time correction</DialogTitle>
          <DialogDescription>Your supervisor will review and apply the correction.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Corrected clock-in</Label>
            <Input type="datetime-local" value={form.requested_clock_in}
              onChange={(e) => setForm({ ...form, requested_clock_in: e.target.value })}
              data-testid="amendment-clockin-input" />
          </div>
          <div>
            <Label className="text-xs">Corrected clock-out</Label>
            <Input type="datetime-local" value={form.requested_clock_out}
              onChange={(e) => setForm({ ...form, requested_clock_out: e.target.value })}
              data-testid="amendment-clockout-input" />
          </div>
          <div>
            <Label className="text-xs">Reason (required)</Label>
            <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="e.g. Forgot to clock out before leaving site"
              data-testid="amendment-reason-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="amendment-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="amendment-submit-btn">
            {busy ? 'Sending…' : 'Send Request'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DailyReview = ({ token, entry, onClose, onSubmitted }) => {
  const { toast } = useToast();
  const [allocations, setAllocations] = useState(null);
  const [breaks, setBreaks] = useState(null);
  const [exceptions, setExceptions] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [showAllocForm, setShowAllocForm] = useState(false);
  const [showAmendment, setShowAmendment] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    try {
      const [a, b, x] = await Promise.all([
        axios.get(`${API}/time/${entry.id}/allocations`, auth),
        axios.get(`${API}/time/breaks/${entry.id}`, auth),
        axios.get(`${API}/time/${entry.id}/exceptions`, auth),
      ]);
      setAllocations(a.data);
      setBreaks(b.data);
      setExceptions(x.data.exceptions || []);
    } catch (e) { /* silent */ }
    try {
      const { data } = await axios.get(`${API}/worker/pickable-jobs`, auth);
      setJobs((data.jobs || []).slice(0, 200));
    } catch (e) { setJobs([]); }
  }, [entry.id]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const total = entryHours(entry);
  const unpaidH = breaks ? breaks.unpaid_minutes / 60 : 0;
  const payable = Math.max(0, total - unpaidH);
  const regular = Math.min(payable, 8);
  const overtime = Math.max(0, payable - 8);

  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/time/${entry.id}/submit`, {}, auth);
      setDone(true);
      onSubmitted();
    } catch (e) {
      toast({ title: 'Could not submit', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="daily-review-dialog">
        {done ? (
          <div className="py-10 text-center space-y-3" data-testid="submit-success-screen">
            <CheckCircle className="w-14 h-14 text-[#00C896] mx-auto" />
            <h3 className="text-xl font-bold text-white">Timesheet submitted</h3>
            <p className="text-sm text-slate-400">Your supervisor will review it before payroll.</p>
            <Button onClick={onClose} className="bg-[#00C896] hover:bg-emerald-600 text-white" data-testid="submit-success-close-btn">Done</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Daily Time Summary — {fmtDate(entry.clock_in)}</DialogTitle>
              <DialogDescription>Review everything, then certify and submit.</DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="bg-[#0F1923] p-3 rounded-lg border border-[#2D3748]">
                <p className="text-xs text-slate-500">Clock In → Out</p>
                <p className="text-white font-semibold">{fmtTime(entry.clock_in)} → {entry.clock_out ? fmtTime(entry.clock_out) : '—'}</p>
              </div>
              <div className="bg-[#0F1923] p-3 rounded-lg border border-[#2D3748]">
                <p className="text-xs text-slate-500">Total Hours</p>
                <p className="text-white font-bold">{total.toFixed(2)}h</p>
              </div>
              <div className="bg-[#0F1923] p-3 rounded-lg border border-[#2D3748]">
                <p className="text-xs text-slate-500">Regular / OT (est.)</p>
                <p className="text-white font-semibold">{regular.toFixed(2)}h / <span className="text-amber-400">{overtime.toFixed(2)}h</span></p>
              </div>
              <div className="bg-[#0F1923] p-3 rounded-lg border border-[#2D3748]">
                <p className="text-xs text-slate-500">Breaks</p>
                <p className="text-white font-semibold">
                  {breaks ? `${Math.round(breaks.unpaid_minutes)}m unpaid · ${Math.round(breaks.paid_minutes)}m paid` : '—'}
                </p>
              </div>
            </div>

            {entry.edited && (
              <p className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2" data-testid="review-edited-notice">
                Times were manually corrected{entry.edited_by_name ? ` by ${entry.edited_by_name}` : ''}{entry.edit_reason ? `: ${entry.edit_reason}` : ''}.
              </p>
            )}

            <div>
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm font-semibold text-white">Job / activity allocations</p>
                <Button size="sm" variant="outline" onClick={() => setShowAllocForm(true)} data-testid="review-add-allocation-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add Allocation
                </Button>
              </div>
              {allocations && (
                <p className={`text-xs mb-2 ${allocations.reconciliation_status === 'balanced' ? 'text-[#00C896]' : 'text-amber-400'}`} data-testid="review-reconciliation">
                  Allocated: {allocations.allocated_hours.toFixed(2)} / {allocations.payroll_hours.toFixed(2)} hrs
                  {allocations.reconciliation_status === 'balanced' ? ' — balanced' : allocations.delta > 0 ? ` — ${allocations.delta.toFixed(2)}h unallocated` : ` — ${Math.abs(allocations.delta).toFixed(2)}h over`}
                </p>
              )}
              <div className="space-y-1">
                {(allocations?.allocations || []).map((a) => (
                  <div key={a.id} className="flex justify-between text-sm bg-[#0F1923] border border-[#2D3748] rounded-lg px-3 py-2" data-testid={`review-allocation-${a.id}`}>
                    <span className="text-slate-300">{a.job_name || 'No job'} · {ACTIVITY_LABELS[a.activity_type] || a.activity_type}{a.activity_description ? ` — ${a.activity_description}` : ''}</span>
                    <span className="text-white font-semibold">{Number(a.hours).toFixed(2)}h</span>
                  </div>
                ))}
                {allocations && allocations.allocations.length === 0 && (
                  <p className="text-xs text-slate-500">No allocations yet — add how you spent this shift.</p>
                )}
              </div>
            </div>

            {exceptions.length > 0 && (
              <div data-testid="review-exceptions">
                <p className="text-sm font-semibold text-white mb-1">Flags on this shift</p>
                <div className="space-y-1">
                  {exceptions.map((x) => (
                    <div key={x.id} className={`text-xs rounded-lg px-3 py-2 border ${x.severity === 'high' ? 'bg-red-500/10 border-red-500/30 text-red-300' : 'bg-amber-500/10 border-amber-500/30 text-amber-300'}`}>
                      {EXCEPTION_EXPLAINERS[x.exception_type] || x.description}
                      {x.status !== 'open' && <span className="ml-1 opacity-70">({x.status})</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p className="text-xs text-slate-400 italic border-t border-[#2D3748] pt-3">
              I certify that the time shown above accurately represents the hours I worked and the activities I performed during this workday.
            </p>

            <div className="space-y-2">
              <Button onClick={submit} disabled={busy || !entry.clock_out}
                className="w-full h-12 bg-[#00C896] hover:bg-emerald-600 text-white font-semibold"
                data-testid="review-submit-certify-btn">
                <CheckCircle className="w-5 h-5 mr-2" />
                {busy ? 'Submitting…' : 'Submit & Certify'}
              </Button>
              <Button onClick={() => setShowAmendment(true)} variant="outline"
                className="w-full h-12 border-amber-500/40 text-amber-300 hover:bg-amber-500/10 font-semibold"
                data-testid="review-request-correction-btn">
                <AlertTriangle className="w-5 h-5 mr-2" />
                Request Correction
              </Button>
            </div>
          </>
        )}
        {showAllocForm && (
          <AllocationForm token={token} entryId={entry.id} jobs={jobs}
            reconciliation={allocations} onAdded={load} onClose={() => setShowAllocForm(false)} />
        )}
        {showAmendment && (
          <AmendmentForm token={token} entry={entry} onClose={() => setShowAmendment(false)} />
        )}
      </DialogContent>
    </Dialog>
  );
};

export const NeedsReview = ({ token }) => {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [reviewing, setReviewing] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/time/my-unsubmitted`, {
        headers: { Authorization: `Bearer ${token}` } });
      setEntries((data.entries || []).slice(0, 5));
      setTotal(data.total || 0);
    } catch (e) { /* silent */ }
  }, [token]);

  useEffect(() => { if (token) load(); }, [token, load]);

  if (total === 0) return null;

  return (
    <Card className="shadow-xl mb-6 border-2 border-amber-500/40" data-testid="needs-review-section">
      <CardContent className="p-6 space-y-3">
        <div className="flex items-center gap-2 text-amber-300 font-semibold" data-testid="needs-review-banner">
          <Clock className="w-5 h-5" />
          {total} shift{total === 1 ? '' : 's'} to review and certify
        </div>
        {entries.map((e) => {
          const hrs = entryHours(e);
          const fullyAllocated = e.total_allocated_hours != null && Math.abs((e.total_allocated_hours || 0) - hrs) <= 0.05;
          const excCount = e.exception_count || 0;
          return (
            <div key={e.id} className="bg-[#0F1923] border border-[#2D3748] rounded-xl p-4 flex items-center justify-between gap-3" data-testid={`review-card-${e.id}`}>
              <div>
                <p className="text-sm text-white font-semibold">{fmtDate(e.clock_in)}</p>
                <p className="text-xs text-slate-400">{fmtTime(e.clock_in)} → {e.clock_out ? fmtTime(e.clock_out) : '—'} · <span className="font-bold text-white">{hrs.toFixed(2)}h</span></p>
                <div className="flex gap-2 mt-1">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${fullyAllocated ? 'bg-emerald-500/15 text-emerald-300' : 'bg-orange-500/15 text-orange-300'}`}>
                    {fullyAllocated ? '✓ Fully allocated' : 'Unallocated time'}
                  </span>
                  {excCount > 0 && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${e.has_unresolved_exceptions ? 'bg-red-500/15 text-red-300' : 'bg-amber-500/15 text-amber-300'}`}>
                      {excCount} exception{excCount === 1 ? '' : 's'}
                    </span>
                  )}
                </div>
              </div>
              <Button size="sm" onClick={() => setReviewing(e)}
                className="bg-[#00C896] hover:bg-emerald-600 text-white font-semibold shrink-0"
                data-testid={`review-certify-btn-${e.id}`}>
                Review & Certify
              </Button>
            </div>
          );
        })}
        {total > entries.length && (
          <p className="text-xs text-slate-500 text-center">…and {total - entries.length} more</p>
        )}
        {reviewing && (
          <DailyReview token={token} entry={reviewing}
            onClose={() => setReviewing(null)} onSubmitted={load} />
        )}
      </CardContent>
    </Card>
  );
};

export default NeedsReview;
